import { PlanTableFormat } from "../../../types/planning/plan.type";
import { toastMessage } from "../../../constants/strings";
import { planningStatus, finalStatus } from "../../../types/status.type";
import { planType } from "../../../constants/RouteTrapTypes";
import type { EditedPlan } from "./EditRouteConfirmDialog";

export function canRemoveDoor(
  status: string,
  currentDoorId: number | null
): boolean {
  if (!currentDoorId) return true;

  const blockedStatuses: string[] = [
    planningStatus.CLDK,
    planningStatus.CLDV,
    planningStatus.TRAP,
    planningStatus.DISP,
    finalStatus.DEPT,
  ];
  return !blockedStatuses.includes(status);
}

export function canRemoveTrailerOrStraightTruck(
  status: string,
  type: PlanTableFormat["type"],
  currentEquipmentId: number | null
): boolean {
  if (!currentEquipmentId) return true;

  if (type === planType.DELIVERY_TRAP) {
    const blockedStatuses: string[] = [
      planningStatus.CLDK,
      planningStatus.TRAP,
    ];
    return !blockedStatuses.includes(status);
  } else {
    return true;
  }
}

export function getRemovalBlockedMessage(
  equipment: "door" | "trailer" | "straightTruck",
  status: string,
  type: PlanTableFormat["type"]
): string {
  if (equipment === "door") {
    if (status === planningStatus.TRAP) {
      return toastMessage.inbound.removeDoorNotAllowedAfterTrap;
    } else if (
      status === planningStatus.DISP ||
      status === planningStatus.CLDV ||
      status === finalStatus.DEPT
    ) {
      return toastMessage.inbound.removeDoorNotAllowedAfterCldv;
    } else if (status === planningStatus.CLDK) {
      return type === planType.DELIVERY_TRAP
        ? "Unable to remove the door when the trap is in CLDK status."
        : toastMessage.inbound.removeDoorNotAllowedFromRoute;
    }
  } else {
    const equipmentName =
      equipment === "trailer" ? "Trailer" : "Straight Truck";

    if (status === planningStatus.TRAP) {
      return toastMessage.inbound.removeTrailerOrStraightTruckNotAllowedFromTrapAfterTrap(
        equipmentName
      );
    } else if (status === planningStatus.CLDK) {
      return `Unable to remove the ${equipmentName} when the trap is in CLDK status.`;
    }
  }

  return "";
}

export function shouldShowDoorRemovalError(
  plan: PlanTableFormat,
  editedPlan: EditedPlan,
  hasAttemptedSubmit: boolean,
  isRemoveEquipmentOrDoorValidationEnabled: boolean
): boolean {
  if (
    !isRemoveEquipmentOrDoorValidationEnabled ||
    !hasAttemptedSubmit ||
    !plan.door
  ) {
    return false;
  }

  const doorRemovalBlocked = !canRemoveDoor(plan.status, plan.door);
  const userTriedToRemoveDoor = editedPlan.doorId === null;

  if (plan.status === planningStatus.CLDK) {
    return doorRemovalBlocked && userTriedToRemoveDoor && !!plan.door;
  }

  const userTriedToChangeDoor = editedPlan.doorId !== plan.door;
  return doorRemovalBlocked && userTriedToChangeDoor;
}

export function shouldShowTrailerRemovalError(
  plan: PlanTableFormat,
  editedPlan: EditedPlan,
  hasAttemptedSubmit: boolean,
  isRemoveEquipmentOrDoorValidationEnabled: boolean
): boolean {
  if (
    !isRemoveEquipmentOrDoorValidationEnabled ||
    !hasAttemptedSubmit ||
    !plan.trailer
  ) {
    return false;
  }

  const trailerRemovalBlocked = !canRemoveTrailerOrStraightTruck(
    plan.status,
    plan.type,
    plan.trailer.id
  );
  const userTriedToRemoveTrailer = editedPlan.trailerEquipmentId === null;

  if (plan.status === planningStatus.CLDK) {
    return trailerRemovalBlocked && userTriedToRemoveTrailer && !!plan.trailer;
  }

  const userTriedToChangeTrailer =
    editedPlan.trailerEquipmentId !== plan.trailer.id;
  return trailerRemovalBlocked && userTriedToChangeTrailer;
}

export function shouldShowStraightTruckRemovalError(
  plan: PlanTableFormat,
  editedPlan: EditedPlan,
  hasAttemptedSubmit: boolean,
  isRemoveEquipmentOrDoorValidationEnabled: boolean
): boolean {
  if (
    !isRemoveEquipmentOrDoorValidationEnabled ||
    !hasAttemptedSubmit ||
    !plan.straightTruck
  ) {
    return false;
  }

  const straightTruckRemovalBlocked = !canRemoveTrailerOrStraightTruck(
    plan.status,
    plan.type,
    plan.straightTruck.id
  );
  const userTriedToRemoveStraightTruck =
    editedPlan.straightTruckEquipmentId === null;

  if (plan.status === planningStatus.CLDK) {
    return (
      straightTruckRemovalBlocked &&
      userTriedToRemoveStraightTruck &&
      !!plan.straightTruck
    );
  }

  const userTriedToChangeStraightTruck =
    editedPlan.straightTruckEquipmentId !== plan.straightTruck.id;
  return straightTruckRemovalBlocked && userTriedToChangeStraightTruck;
}
